import React, { useEffect, useState, useRef } from 'react';
import { Tooltip } from 'antd';
import styled, { keyframes, css } from 'styled-components';

import StepLimitModal from '../StepLimitModal';
import { MAX_STEPS_PER_ROUND } from '../../constants/game';

// ============================================
// DESIGN TOKENS
// ============================================
const tokens = {
    colors: {
        surface: 'rgba(18, 18, 20, 0.92)',
        surfaceHover: 'rgba(28, 28, 32, 0.95)',
        border: 'rgba(255, 255, 255, 0.08)',
        borderHover: 'rgba(255, 255, 255, 0.15)',
        text: '#ffffff',
        textMuted: 'rgba(255, 255, 255, 0.6)',
        accent: '#c80707',
        accentGlow: 'rgba(200, 7, 7, 0.4)',
        success: '#22c55e',
    },
    blur: '20px',
    radius: '12px',
    shadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)',
};

// ============================================
// ANIMATIONS
// ============================================
const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const pulseGlow = keyframes`
    0%, 100% {
        box-shadow: 0 0 0 0 ${tokens.colors.accentGlow};
    }
    50% {
        box-shadow: 0 0 20px 4px ${tokens.colors.accentGlow};
    }
`;

const countUp = keyframes`
    from {
        transform: scale(1.15);
        color: ${tokens.colors.accent};
    }
    to {
        transform: scale(1);
        color: ${tokens.colors.text};
    }
`;

const limitPulse = keyframes`
    0%, 100% {
        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4),
                    0 8px 32px rgba(0, 0, 0, 0.4);
        border-color: rgba(239, 68, 68, 0.6);
    }
    50% {
        box-shadow: 0 0 20px 4px rgba(239, 68, 68, 0.6),
                    0 8px 32px rgba(0, 0, 0, 0.4);
        border-color: rgba(239, 68, 68, 0.9);
    }
`;

// ============================================
// STYLED COMPONENTS
// ============================================
const HUDContainer = styled.div`
    position: fixed;
    top: 80px;
    left: 20px;
    z-index: 100;
    display: flex;
    flex-direction: column;
    gap: 12px;
    animation: ${fadeInUp} 0.5s ease-out;
    pointer-events: none;

    @media (max-width: 960px) {
        display: none;
    }
`;

const HUDCard = styled.div`
    background: ${tokens.colors.surface};
    backdrop-filter: blur(${tokens.blur});
    -webkit-backdrop-filter: blur(${tokens.blur});
    border: 1px solid ${tokens.colors.border};
    border-radius: ${tokens.radius};
    box-shadow: ${tokens.shadow};
    padding: 16px 20px;
    min-width: 140px;
    pointer-events: auto;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
        background: ${tokens.colors.surfaceHover};
        border-color: ${tokens.colors.borderHover};
        transform: translateX(4px);
    }
`;

const RoundCard = styled(HUDCard)`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const RoundIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    background: linear-gradient(135deg, ${tokens.colors.accent} 0%, #a00606 100%);
    border-radius: 10px;
    font-family: 'JetBrains Mono', 'SF Mono', 'Consolas', monospace;
    font-size: 18px;
    font-weight: 700;
    color: ${tokens.colors.text};
    box-shadow: 0 4px 12px ${tokens.colors.accentGlow};
`;

const RoundInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const RoundLabel = styled.span`
    font-family: 'Work Sans', sans-serif;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${tokens.colors.textMuted};
`;

const RoundText = styled.span`
    font-family: 'Work Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: ${tokens.colors.text};
`;

const ScoreCard = styled(HUDCard)`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ScoreHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ScoreIcon = styled.div`
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${tokens.colors.accent};
`;

const ScoreLabel = styled.span`
    font-family: 'Work Sans', sans-serif;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${tokens.colors.textMuted};
`;

const ScoreValue = styled.div<{ $animate?: boolean }>`
    font-family: 'JetBrains Mono', 'SF Mono', 'Consolas', monospace;
    font-size: 28px;
    font-weight: 700;
    color: ${tokens.colors.text};
    letter-spacing: -0.02em;
    animation: ${props => props.$animate ? countUp : 'none'} 0.4s ease-out;
`;

const ScoreUnit = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${tokens.colors.textMuted};
    margin-left: 4px;
`;

const ProgressBar = styled.div`
    width: 100%;
    height: 4px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
    overflow: hidden;
    margin-top: 4px;
`;

const ProgressFill = styled.div<{ $percent: number; $warning?: boolean }>`
    height: 100%;
    width: ${props => props.$percent}%;
    background: ${props => props.$warning
        ? 'linear-gradient(90deg, #f59e0b 0%, #ef4444 100%)'
        : `linear-gradient(90deg, ${tokens.colors.accent} 0%, #ff4444 100%)`};
    border-radius: 2px;
    transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`;

const StepsCard = styled(HUDCard)<{ $limit?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 6px;

    ${props => props.$limit && css`
        background: rgba(239, 68, 68, 0.15);
        border-color: rgba(239, 68, 68, 0.6);
        animation: ${limitPulse} 1.5s ease-in-out infinite;

        &:hover {
            background: rgba(239, 68, 68, 0.2);
            border-color: rgba(239, 68, 68, 0.8);
        }
    `}
`;

const StepsValue = styled.div<{ $warning?: boolean; $limit?: boolean }>`
    font-family: 'JetBrains Mono', 'SF Mono', 'Consolas', monospace;
    font-size: 20px;
    font-weight: 700;
    color: ${props => props.$limit ? '#ef4444' : props.$warning ? '#f59e0b' : tokens.colors.text};
    letter-spacing: -0.02em;
`;

// ============================================
// COMPONENT
// ============================================
interface GameHUDProps {
    round: number;
    totalRounds: number;
    totalScore: number;
    maxPossibleScore?: number;
    isLoading?: boolean;
    stepCount?: number;
    maxSteps?: number;
}

export const GameHUD: React.FC<GameHUDProps> = ({
    round,
    totalRounds,
    totalScore,
    maxPossibleScore = 500,
    isLoading = false,
    stepCount = 0,
    maxSteps = MAX_STEPS_PER_ROUND,
}) => {
    const [stepLimitModalVisible, setStepLimitModalVisible] = useState(false);
    const wasLimitReachedRef = useRef(false);
    const lastRoundRef = useRef(round);

    const scorePercent = Math.min((totalScore / maxPossibleScore) * 100, 100);
    const stepsPercent = Math.min((stepCount / maxSteps) * 100, 100);
    const isStepsWarning = stepCount >= maxSteps * 0.8; // 80% = varování
    const isStepsLimit = stepCount >= maxSteps;

    // Zobrazit modál když je poprvé dosažen limit kroků
    useEffect(() => {
        // Reset při změně kola
        if (round !== lastRoundRef.current) {
            lastRoundRef.current = round;
            wasLimitReachedRef.current = false;
        }

        // Zobrazit modál pouze jednou za kolo
        if (isStepsLimit && !wasLimitReachedRef.current) {
            wasLimitReachedRef.current = true;
            setStepLimitModalVisible(true);
        }
    }, [isStepsLimit, round]);

    return (
        <HUDContainer>
            {/* Round Indicator */}
            <RoundCard>
                <RoundIndicator>{round}</RoundIndicator>
                <RoundInfo>
                    <RoundLabel>Kolo</RoundLabel>
                    <RoundText>{round} / {totalRounds}</RoundText>
                </RoundInfo>
            </RoundCard>

            {/* Score Display */}
            <ScoreCard>
                <ScoreHeader>
                    <ScoreIcon>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                    </ScoreIcon>
                    <ScoreLabel>Celkové skóre</ScoreLabel>
                </ScoreHeader>
                <ScoreValue $animate={totalScore > 0}>
                    {Math.round(totalScore)}
                    <ScoreUnit>bodů</ScoreUnit>
                </ScoreValue>
                <ProgressBar>
                    <ProgressFill $percent={scorePercent} />
                </ProgressBar>
            </ScoreCard>

            {/* Steps Counter */}
            <Tooltip
                title={isStepsLimit
                    ? "Limit kroků vyčerpán! Umístěte svůj tip na mapu."
                    : "V každém kole máte k dispozici omezený počet kroků. Jakmile je vyčerpáte, navigace v panoramatu se zablokuje a nezbude vám nic jiného než umístit svůj tip."
                }
                placement="right"
                mouseEnterDelay={0.3}
            >
                <StepsCard $limit={isStepsLimit}>
                    <ScoreHeader>
                        <ScoreIcon style={isStepsLimit ? { color: '#ef4444' } : undefined}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                            </svg>
                        </ScoreIcon>
                        <ScoreLabel style={isStepsLimit ? { color: '#ef4444' } : undefined}>
                            {isStepsLimit ? 'Limit vyčerpán!' : 'Kroky v kole'}
                        </ScoreLabel>
                    </ScoreHeader>
                    <StepsValue $warning={isStepsWarning} $limit={isStepsLimit}>
                        {stepCount} / {maxSteps}
                    </StepsValue>
                    <ProgressBar>
                        <ProgressFill $percent={stepsPercent} $warning={isStepsWarning} />
                    </ProgressBar>
                </StepsCard>
            </Tooltip>

            {/* Step Limit Modal */}
            <StepLimitModal
                visible={stepLimitModalVisible}
                onClose={() => setStepLimitModalVisible(false)}
            />
        </HUDContainer>
    );
};

export default GameHUD;
